/**
 * Rental block types and utility functions
 * These are pure functions with no database access, safe for client components
 */

import type { ProductRentalBlock, BranchOperationBlock } from '@/types';
import { parseDateOnly } from '@/lib/reservation-utils';

interface DateParts {
  dateStr: string;
  dayOfWeek: number;
  dayOfMonth: number;
}

function dateParts(date: Date): DateParts {
  // Use local date instead of UTC to avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return {
    dateStr: `${year}-${month}-${day}`,
    dayOfWeek: date.getDay(),
    dayOfMonth: date.getDate(),
  };
}

/**
 * Whether a block (product or branch) is active on the given date, honoring its
 * repeat_type. Shared by every block check so the repeat rules stay in one place.
 */
function isBlockActiveOn(
  block: { start_date: string; end_date: string; repeat_type: string; weekly_days?: number[]; monthly_day?: number },
  date: Date,
  parts: DateParts
): boolean {
  if (!(parts.dateStr >= block.start_date && parts.dateStr <= block.end_date)) {
    return false;
  }
  if (block.repeat_type === 'none') return true;
  if (block.repeat_type === 'weekly' && block.weekly_days) {
    return block.weekly_days.includes(parts.dayOfWeek);
  }
  if (block.repeat_type === 'monthly' && block.monthly_day) {
    return parts.dayOfMonth === block.monthly_day;
  }
  if (block.repeat_type === 'yearly') {
    const blockDate = parseDateOnly(block.start_date);
    return (
      date.getMonth() === blockDate.getMonth() &&
      date.getDate() === blockDate.getDate()
    );
  }
  return false;
}

function isSlotScoped(block: ProductRentalBlock): boolean {
  return Array.isArray(block.timeslot_orders) && block.timeslot_orders.length > 0;
}

/**
 * Whether a product block applies to the product + fulfilment target the customer
 * picked. Single source of the rule; every product-block matcher below goes
 * through it, so callers may pass unfiltered rows.
 *
 * `branchId` is either a real branch uuid, "" (nothing picked yet), or one of the
 * booking flow's method sentinels "delivery"/"shipping" (see useBookingDateBlocking).
 *
 * Product scope:
 * - `product_ids` lists products → matches only those products (always the case
 *   for whole-product rows).
 * - `product_ids` empty/absent AND `method_type` set → 파트너 전체 제한: every
 *   product of that partner, including ones created later.
 * - `product_ids` empty/absent AND no `method_type` → matches nothing. Such a row
 *   should not exist; failing closed here keeps a bad row from blocking a whole
 *   partner outright.
 *
 * Fulfilment scope:
 * - `method_type` set (택배 제한 / 숙소배송 제한): the row scopes ONE fulfilment
 *   method, so it applies only when the customer chose that method. Such rows
 *   always carry branch_id = null, so no branch check is needed.
 * - `method_type` null/undefined: legacy whole-product row — branch_id null means
 *   "applies at every branch", a value means only that branch.
 */
export function productBlockAppliesTo(
  block: ProductRentalBlock,
  productId: string,
  branchId: string
): boolean {
  const listedProducts = block.product_ids;
  if (Array.isArray(listedProducts) && listedProducts.length > 0) {
    if (!listedProducts.includes(productId)) return false;
  } else if (!block.method_type) {
    // Partner-wide scope is only ever granted to method-scoped rows.
    return false;
  }

  if (block.method_type) return branchId === block.method_type;
  return !block.branch_id || block.branch_id === branchId;
}

const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * The PostgREST `.or()` filter that loads a product's own rental blocks PLUS the
 * partner's method-scoped rows (택배 제한), which may be partner-wide
 * (`product_ids = []`) and therefore invisible to a `product_ids` containment
 * filter.
 *
 * Returns the filter string, or `null` when it cannot be built safely — the
 * caller then keeps its plain `.contains("product_ids", [productId])` filter.
 *
 * Notes on the predicate:
 * - `product_ids.cs.{<uuid>}` is the array-containment filter the callers used
 *   before; the first branch is byte-for-byte the old behaviour.
 * - The second branch deliberately does NOT try to express "product_ids is
 *   empty" in PostgREST. An empty-array literal is awkward to express portably
 *   in a `.or()` string, and it is unnecessary: `productBlockAppliesTo` above
 *   already decides product scope, so a partner method row that *does* list
 *   products is simply filtered out in memory. The cost is over-fetching the
 *   partner's method-scoped rows, of which there are very few.
 * - Ids are uuid-checked because `.or()` takes a raw, comma/parenthesis
 *   delimited string — an unvalidated id could otherwise inject extra filters.
 */
export function buildProductBlocksOrFilter(
  productId: string,
  partnerId: string | null | undefined
): string | null {
  if (!partnerId) return null;
  if (!UUID_RE.test(productId) || !UUID_RE.test(partnerId)) return null;
  return `product_ids.cs.{${productId}},and(partner_id.eq.${partnerId},method_type.not.is.null)`;
}

/**
 * The `branchId` to feed the matchers below when the branch and the fulfilment
 * method are stored separately (cart rows, checkout input) rather than as the
 * product page's single pseudo-branch value.
 *
 * A physical branch id wins. Otherwise a 택배/숙소배송 method becomes the pseudo
 * branch id so method-scoped rows (`method_type`) can match. Anything else → ""
 * (no branch scope), which is the pre-existing behaviour.
 */
export function resolveBlockScopeId(
  branchId: string | null | undefined,
  methodType: string | null | undefined
): string {
  // Already a real branch id or a "delivery"/"shipping" sentinel — pass through.
  if (branchId) return branchId;
  if (methodType === 'delivery' || methodType === 'shipping') return methodType;
  return '';
}

/**
 * Check if a date is blocked for rental (i.e. the whole date is unavailable).
 *
 * Slot-scoped product blocks (timeslot_orders set) do NOT block the whole date —
 * they only disable specific timeslots, so they are ignored here. Use
 * getBlockedTimeslotOrders / areAllTimeslotsBlocked for per-slot logic.
 *
 * This is a pure function with no side effects.
 */
export function isDateBlocked(
  date: Date,
  productId: string,
  branchId: string,
  productBlocks: ProductRentalBlock[],
  branchBlocks: BranchOperationBlock[]
): boolean {
  const parts = dateParts(date);

  // Check product rental blocks (whole-date only — skip slot-scoped)
  for (const block of productBlocks) {
    if (!productBlockAppliesTo(block, productId, branchId)) continue;
    if (isSlotScoped(block)) continue;
    if (isBlockActiveOn(block, date, parts)) return true;
  }

  // Check branch operation blocks
  for (const block of branchBlocks) {
    if (block.branch_id !== branchId) continue;
    if (isBlockActiveOn(block, date, parts)) return true;
  }

  return false;
}

/**
 * Returns the set of timeslot display_order values blocked on `date` for the
 * given product/branch (only slot-scoped product blocks contribute).
 */
export function getBlockedTimeslotOrders(
  date: Date,
  productId: string,
  branchId: string,
  productBlocks: ProductRentalBlock[]
): Set<number> {
  const parts = dateParts(date);
  const blocked = new Set<number>();
  for (const block of productBlocks) {
    if (!productBlockAppliesTo(block, productId, branchId)) continue;
    if (!isSlotScoped(block)) continue;
    if (isBlockActiveOn(block, date, parts)) {
      for (const order of block.timeslot_orders ?? []) blocked.add(order);
    }
  }
  return blocked;
}

/**
 * For a timeslot product, whether EVERY timeslot is unavailable on `date` — i.e.
 * the date should be disabled in the calendar. True when a whole-date block
 * applies, or when slot-scoped blocks together cover all of `allOrders`.
 */
export function areAllTimeslotsBlocked(
  date: Date,
  productId: string,
  branchId: string,
  productBlocks: ProductRentalBlock[],
  branchBlocks: BranchOperationBlock[],
  allOrders: number[]
): boolean {
  // A whole-date product/branch block closes everything.
  if (isDateBlocked(date, productId, branchId, productBlocks, branchBlocks)) {
    return true;
  }
  if (allOrders.length === 0) return false;
  const blocked = getBlockedTimeslotOrders(date, productId, branchId, productBlocks);
  return allOrders.every((order) => blocked.has(order));
}

/**
 * Check if ANY date in a range [startDate, endDate] is blocked (inclusive).
 * Used for listing-level availability checks.
 *
 * Both checkin and checkout dates are rental days (대여 모델: +1 day).
 * For daily rentals: checks all dates in the range.
 * For timeslot rentals: call isDateBlocked with startDate only.
 */
export function isDateRangeBlocked(
  startDate: Date,
  endDate: Date,
  productId: string,
  branchId: string,
  productBlocks: ProductRentalBlock[],
  branchBlocks: BranchOperationBlock[]
): boolean {
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  while (current <= end) {
    if (isDateBlocked(current, productId, branchId, productBlocks, branchBlocks)) {
      return true;
    }
    current.setDate(current.getDate() + 1);
  }

  return false;
}
